import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import CloseIcon from '@mui/icons-material/Close'
import ArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import services from '../../services'
import { useNotification } from '../../hooks/useNotification.js'
import Janela from '../componentes/Janela'
import BuscaPessoa, { PessoaAchada } from '../componentes/BuscaPessoa'
import { plural } from './comum'
import './gestao.scss'

interface Agente {
  id: number; name: string; instructions?: string | null; azureAgentId?: string | null
  snowflakeDatabase?: string | null; snowflakeSchema?: string | null; snowflakeAgent?: string | null
  monthlyTokenLimit?: number | null; suggestions?: string[] | null
}
interface Form { nome: string; db: string; schema: string; agente: string; instrucoes: string; limite: string; sugestoes: string[] }
interface Liberacao { usuarioId: number; nome: string; email?: string | null; limiteMensal?: number | null; usadoNoMes: number }
interface Consumo { usuarioId: number; nome: string; respostas: number; erros: number; tokensTotal: number; limite?: number | null; ultimoUso?: string | null }

const IDENT = /^[A-Za-z_][A-Za-z0-9_$]{0,254}$/
const nf = new Intl.NumberFormat('pt-BR')
const paraForm = (a: Agente): Form => ({
  nome: a.name, db: a.snowflakeDatabase || '', schema: a.snowflakeSchema || '', agente: a.snowflakeAgent || '',
  instrucoes: a.instructions || '', limite: a.monthlyTokenLimit ? String(a.monthlyTokenLimit) : '', sugestoes: a.suggestions || []
})
const lerLimite = (t: string): number | null | 'invalido' => {
  const s = t.replace(/\D/g, '')
  if (!t.trim()) return null
  return s && Number(s) > 0 ? Number(s) : 'invalido'
}

/** Gestão › Agentes oneflow: o Cortex Agent de cada um, quem pode perguntar e quanto usou. */
export default function Agentes() {
  const { id } = useParams()
  const atual = Number(id) || null
  const navigate = useNavigate()
  const { notify } = useNotification()
  const [lista, setLista] = useState<Agente[] | null>(null)
  const [busca, setBusca] = useState('')
  const [form, setForm] = useState<Form | null>(null)
  const [agente, setAgente] = useState<Agente | null>(null)
  const [novo, setNovo] = useState<Form | null>(null)
  const [excluir, setExcluir] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [erroJanela, setErroJanela] = useState<string | null>(null)

  const carregarLista = useCallback(async () => {
    const r = await services.api.getAllAgents()
    setLista(r.fail ? [] : (r.data || []).sort((a: Agente, b: Agente) => a.name.localeCompare(b.name)))
  }, [])
  const carregarAgente = useCallback(async () => {
    if (!atual) { setAgente(null); setForm(null); return }
    const r = await services.api.getAgent(atual)
    if (r.fail || !r.data) { setAgente(null); setForm(null); return }
    setAgente(r.data); setForm(paraForm(r.data))
  }, [atual])

  useEffect(() => { carregarLista() }, [carregarLista])
  useEffect(() => { carregarAgente() }, [carregarAgente])
  useEffect(() => {
    if (!atual && lista?.length && window.innerWidth > 768) navigate(`/app/gestao/agentes/${lista[0].id}`, { replace: true })
  }, [atual, lista, navigate])

  const q = busca.trim().toLowerCase()
  const visiveis = (lista || []).filter(a => !q || `${a.name} ${a.snowflakeAgent}`.toLowerCase().includes(q))
  const fabric = !!agente?.azureAgentId && !agente.snowflakeAgent
  const mudou = !!agente && !!form && JSON.stringify(form) !== JSON.stringify(paraForm(agente))
  const problema = (f: Form | null) => {
    if (!f) return 'x'
    if (!f.nome.trim()) return 'Dê um nome ao agente.'
    if (!fabricDe(f) && ![f.db, f.schema, f.agente].every(v => IDENT.test(v.trim()))) return 'Banco, schema e agente aceitam letras, números, _ e $, começando por letra ou _.'
    if (lerLimite(f.limite) === 'invalido') return 'O limite mensal precisa ser um número maior que zero (ou vazio, sem limite).'
    if (f.sugestoes.some(s => s.trim().length > 200)) return 'Cada pergunta sugerida tem até 200 caracteres.'
    return null
  }
  const fabricDe = (f: Form) => f === form && fabric

  const corpo = (f: Form, idAgente?: number) => {
    const limite = lerLimite(f.limite)
    return {
      ...(idAgente ? { id: idAgente } : {}),
      name: f.nome.trim(), instructions: f.instrucoes.trim() || null,
      azureAgentId: fabric && idAgente ? agente!.azureAgentId : null,
      snowflakeDatabase: f.db.trim() || null, snowflakeSchema: f.schema.trim() || null, snowflakeAgent: f.agente.trim().toUpperCase() || null,
      monthlyTokenLimit: limite === 'invalido' ? null : limite,
      suggestions: f.sugestoes.map(s => s.trim()).filter(Boolean)
    }
  }

  const salvar = async () => {
    if (!form || !agente) return
    const r = await services.api.updateAiAgent(corpo(form, agente.id))
    if (r.fail) { notify(r.error || 'Não foi possível salvar o agente.', { variant: 'error' }); return }
    notify('Agente salvo. Vale para todos os dashboards que usam ele.', { variant: 'success' })
    carregarAgente(); carregarLista()
  }

  const criar = async () => {
    if (!novo) return
    setSalvando(true); setErroJanela(null)
    const r = await services.api.createNewAiAgent(corpo(novo))
    setSalvando(false)
    if (r.fail) { setErroJanela(r.error || 'Não foi possível criar o agente.'); return }
    setNovo(null)
    notify('Agente criado. Libere as pessoas e ligue nos dashboards.', { variant: 'success' })
    await carregarLista()
    if (r.data?.id) navigate(`/app/gestao/agentes/${r.data.id}`)
  }

  const confirmarExcluir = async () => {
    if (!agente) return
    setSalvando(true)
    const r = await services.api.deleteAiAgent(agente.id)
    setSalvando(false)
    if (r.fail) { notify(r.error || 'Não foi possível excluir.', { variant: 'error' }); return }
    setExcluir(false)
    notify(`Agente ${agente.name} excluído.`, { variant: 'success' })
    await carregarLista()
    navigate('/app/gestao/agentes')
  }

  const moverSugestao = (i: number, para: number) => {
    if (!form) return
    const s = [...form.sugestoes]
    ;[s[i], s[para]] = [s[para], s[i]]
    setForm({ ...form, sugestoes: s })
  }

  return (
    <div className="ng">
      <div className="ng-cab">
        <h1>Agentes <span className="ng-oneflow"><b>one</b><i>flow</i></span></h1>
        <div className="ng-acoes"><button type="button" className="ng-btn prim" onClick={() => { setErroJanela(null); setNovo({ nome: '', db: '', schema: '', agente: '', instrucoes: '', limite: '', sugestoes: [] }) }}><AddIcon />Novo agente</button></div>
      </div>
      <p className="ng-explic">Cada agente é um Cortex Agent do Snowflake. Para aparecer num dashboard, ligue o agente na página do dashboard no ambiente e libere as pessoas aqui.</p>

      <div className={`ng-ambientes${atual ? ' com-detalhe' : ''}`}>
        <aside className="ng-cartao ng-amb-lista" aria-label="Agentes">
          <label className="ng-busca"><SearchIcon fontSize="small" />
            <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar agente" aria-label="Buscar agente" />
          </label>
          <div className="ng-amb-itens">
            {!lista && <p className="ng-explic">Carregando…</p>}
            {lista && visiveis.length === 0 && <p className="ng-explic">Nenhum agente.</p>}
            {visiveis.map(a => (
              <button key={a.id} type="button" className="ng-amb-item" aria-current={a.id === atual ? 'page' : undefined} onClick={() => navigate(`/app/gestao/agentes/${a.id}`)}>
                <span className="ng-amb-ini">{a.name.charAt(0)}</span>
                <span className="ng-amb-txt"><b>{a.name}</b><small>{a.snowflakeAgent ? `${a.snowflakeDatabase}.${a.snowflakeSchema}.${a.snowflakeAgent}` : 'Fabric (antigo)'}</small></span>
              </button>
            ))}
          </div>
        </aside>

        <section className="ng-amb-detalhe">
          {atual && !form && <div className="ng-cartao"><p className="ng-explic">Carregando…</p></div>}
          {agente && form && (
            <>
              <div className="ng-cartao">
                <div className="ng-cartao-cab">
                  <button type="button" className="ng-btn ng-voltar" onClick={() => navigate('/app/gestao/agentes')}>‹ Agentes</button>
                  <h2>{agente.name}</h2>
                  <div className="ng-acoes"><button type="button" className="ng-btn perigo" onClick={() => setExcluir(true)}>Excluir</button></div>
                </div>
                {fabric && <p className="ng-frase">Este agente ainda é do Fabric (antigo). Preencha banco, schema e agente do Snowflake para migrar.</p>}
                <CamposAgente f={form} mudar={setForm} />
                <div className="ng-campo" style={{ marginTop: 14 }}>
                  <span>Perguntas sugeridas (até 10)</span>
                  <small>Aparecem como atalho quando a pessoa abre o oneflow. A primeira é a mais à mostra.</small>
                  {form.sugestoes.map((s, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input style={{ flex: 1 }} value={s} maxLength={200} onChange={e => setForm({ ...form, sugestoes: form.sugestoes.map((x, j) => (j === i ? e.target.value : x)) })} aria-label={`Pergunta ${i + 1}`} />
                      <button type="button" className="ng-ico" disabled={i === 0} onClick={() => moverSugestao(i, i - 1)} aria-label="Subir"><ArrowUpIcon fontSize="small" /></button>
                      <button type="button" className="ng-ico" disabled={i === form.sugestoes.length - 1} onClick={() => moverSugestao(i, i + 1)} aria-label="Descer"><ArrowDownIcon fontSize="small" /></button>
                      <button type="button" className="ng-ico perigo" onClick={() => setForm({ ...form, sugestoes: form.sugestoes.filter((_, j) => j !== i) })} aria-label="Remover pergunta"><CloseIcon fontSize="small" /></button>
                    </div>
                  ))}
                  {form.sugestoes.length < 10 && <button type="button" className="ng-btn" style={{ alignSelf: 'flex-start' }} onClick={() => setForm({ ...form, sugestoes: [...form.sugestoes, ''] })}><AddIcon />Adicionar pergunta</button>}
                </div>
                {mudou && (
                  <div className="ng-rodape-cartao">
                    <button type="button" className="ng-btn prim" disabled={!!problema(form)} onClick={salvar}>Salvar alterações</button>
                    <button type="button" className="ng-btn" onClick={() => setForm(paraForm(agente))}>Desfazer</button>
                    {problema(form) && <small style={{ color: '#c4362b', fontWeight: 700 }}>{problema(form)}</small>}
                  </div>
                )}
              </div>
              {!fabric && <LiberacoesEConsumo agente={agente} />}
            </>
          )}
        </section>
      </div>

      <Janela aberto={!!novo} titulo="Novo agente" sub="Um Cortex Agent do Snowflake que responde no oneflow" botao="Criar agente" salvando={salvando} erro={erroJanela}
              desabilitado={!!problema(novo)} aoConfirmar={criar} aoFechar={() => setNovo(null)}>
        {novo && <CamposAgente f={novo} mudar={setNovo} compacto />}
      </Janela>

      <Janela aberto={excluir} titulo="Excluir agente" botao="Excluir de vez" perigo salvando={salvando} aoConfirmar={confirmarExcluir} aoFechar={() => setExcluir(false)}>
        <p style={{ margin: 0, lineHeight: 1.5 }}>Excluir <b>{agente?.name}</b>? O oneflow some dos dashboards que usam este agente, e as liberações das pessoas se perdem. O histórico de consumo fica.</p>
      </Janela>
    </div>
  )
}

function CamposAgente({ f, mudar, compacto }: { f: Form; mudar: (f: Form) => void; compacto?: boolean }) {
  const cls = compacto ? 'nv-campo' : 'ng-campo'
  return (
    <>
      <div className={compacto ? 'nv-campos' : 'ng-campos'}>
        <label className={`${cls} largo`}><span>Nome no portal</span><input value={f.nome} maxLength={120} onChange={e => mudar({ ...f, nome: e.target.value })} placeholder="Ex.: Ruptura e abastecimento" /></label>
        <label className={cls}><span>Banco (database)</span><input value={f.db} onChange={e => mudar({ ...f, db: e.target.value })} placeholder="Ex.: TRADE_DW" /></label>
        <label className={cls}><span>Schema</span><input value={f.schema} onChange={e => mudar({ ...f, schema: e.target.value })} placeholder="Ex.: ONEFLOW" /></label>
        <label className={cls}><span>Agente</span><input value={f.agente} onChange={e => mudar({ ...f, agente: e.target.value })} placeholder="Ex.: RUPTURA" /></label>
        <label className={cls}><span>Limite mensal do agente (tokens)</span><input value={f.limite} inputMode="numeric" onChange={e => mudar({ ...f, limite: e.target.value.replace(/\D/g, '') })} placeholder="Sem limite" /></label>
      </div>
      <label className={cls} style={{ marginTop: 12 }}><span>Instruções</span>
        <textarea rows={compacto ? 3 : 5} maxLength={4000} value={f.instrucoes} onChange={e => mudar({ ...f, instrucoes: e.target.value })} placeholder="Como o agente deve responder: tom, formato, o que evitar." />
        <small>Vão junto com cada pergunta, antes do recorte de dados de quem pergunta.</small>
      </label>
    </>
  )
}

function LiberacoesEConsumo({ agente }: { agente: Agente }) {
  const { notify } = useNotification()
  const hoje = new Date()
  const [mes, setMes] = useState(`${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`)
  const [liberacoes, setLiberacoes] = useState<Liberacao[] | null>(null)
  const [consumo, setConsumo] = useState<Consumo[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [busca, setBusca] = useState('')
  const [liberar, setLiberar] = useState<{ pessoa: PessoaAchada | null; limite: string } | null>(null)
  const [limites, setLimites] = useState<Record<number, string>>({})

  const carregar = useCallback(async () => {
    const r = await services.api.oneflowLiberacoes(agente.id)
    if (r.fail) { setErro(r.status === 401 || r.status === 403 ? 'Só administradores do BI liberam pessoas e veem o consumo.' : r.error || 'Não foi possível carregar.'); return }
    setErro(null)
    setLiberacoes((r.data || []).sort((a: Liberacao, b: Liberacao) => a.nome.localeCompare(b.nome)))
  }, [agente.id])
  useEffect(() => { carregar() }, [carregar])
  useEffect(() => {
    const [a, m] = mes.split('-').map(Number)
    services.api.oneflowConsumo(agente.id, a, m).then((r: any) => setConsumo(r.fail ? [] : r.data || []))
  }, [agente.id, mes])

  const gravarLimite = async (l: Liberacao) => {
    const v = lerLimite(limites[l.usuarioId] ?? '')
    if (v === 'invalido') { notify('O limite precisa ser um número maior que zero.', { variant: 'warning' }); return }
    const r = await services.api.oneflowLiberar(agente.id, l.usuarioId, v)
    if (r.fail) { notify(r.error || 'Não foi possível salvar o limite.', { variant: 'error' }); return }
    notify(`Limite de ${l.nome} salvo.`, { variant: 'success' })
    setLimites(x => { const n = { ...x }; delete n[l.usuarioId]; return n })
    carregar()
  }
  const tirar = async (l: Liberacao) => {
    const r = await services.api.oneflowTirarLiberacao(agente.id, l.usuarioId)
    if (r.fail) { notify(r.error || 'Não foi possível tirar.', { variant: 'error' }); return }
    notify(`${l.nome} não pergunta mais a este agente.`, { variant: 'success' })
    carregar()
  }
  const confirmarLiberar = async () => {
    if (!liberar?.pessoa) return
    const v = lerLimite(liberar.limite)
    const r = await services.api.oneflowLiberar(agente.id, liberar.pessoa.id, v === 'invalido' ? null : v)
    if (r.fail) { notify(r.error || 'Não foi possível liberar.', { variant: 'error' }); return }
    notify(`${liberar.pessoa.nome} liberada.`, { variant: 'success' })
    setLiberar(null)
    carregar()
  }

  if (erro) return <div className="ng-cartao"><h2>Quem pode perguntar</h2><p className="ng-explic">{erro}</p></div>
  const q = busca.trim().toLowerCase()
  const visiveis = (liberacoes || []).filter(l => !q || `${l.nome} ${l.email}`.toLowerCase().includes(q))
  const totais = (consumo || []).reduce((s, c) => ({ respostas: s.respostas + c.respostas, tokens: s.tokens + c.tokensTotal }), { respostas: 0, tokens: 0 })

  return (
    <>
      <div className="ng-cartao">
        <div className="ng-cartao-cab">
          <h2>Quem pode perguntar ({liberacoes?.length ?? '…'})</h2>
          <div className="ng-acoes"><button type="button" className="ng-btn prim" onClick={() => setLiberar({ pessoa: null, limite: '' })}><AddIcon />Liberar pessoa</button></div>
        </div>
        <p className="ng-explic">Além de liberada aqui, a pessoa precisa ter acesso ao dashboard onde o agente está ligado. Administradores do BI não precisam ser liberados.</p>
        {(liberacoes?.length || 0) > 8 && (
          <div className="ng-filtros" style={{ marginBottom: 8 }}>
            <label className="ng-busca"><SearchIcon fontSize="small" /><input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar pessoa" aria-label="Buscar pessoa liberada" /></label>
          </div>
        )}
        <div className="ng-tabela">
          <table>
            <thead><tr><th>Pessoa</th><th>Limite por mês</th><th className="n">Usado no mês</th><th /></tr></thead>
            <tbody>
              {liberacoes?.length === 0 && <tr><td colSpan={4} className="vazio">Ninguém liberado ainda.</td></tr>}
              {visiveis.map(l => {
                const editando = limites[l.usuarioId] !== undefined
                return (
                  <tr key={l.usuarioId}>
                    <td><b>{l.nome}</b><small style={{ display: 'block', color: '#8b92a1' }}>{l.email}</small></td>
                    <td>
                      <input className="ng-select" style={{ width: 130 }} inputMode="numeric" placeholder="Sem limite" aria-label={`Limite de ${l.nome}`}
                             value={editando ? limites[l.usuarioId] : (l.limiteMensal ? String(l.limiteMensal) : '')}
                             onChange={e => setLimites(x => ({ ...x, [l.usuarioId]: e.target.value.replace(/\D/g, '') }))} />
                      {editando && <button type="button" className="ng-btn" style={{ marginLeft: 6 }} onClick={() => gravarLimite(l)}>Salvar</button>}
                    </td>
                    <td className="n">{nf.format(l.usadoNoMes)}{l.limiteMensal ? ` (${Math.round(l.usadoNoMes * 100 / l.limiteMensal)}%)` : ''}</td>
                    <td style={{ textAlign: 'right' }}><button type="button" className="ng-ico perigo" onClick={() => tirar(l)} aria-label={`Tirar ${l.nome}`}><CloseIcon fontSize="small" /></button></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ng-cartao">
        <div className="ng-cartao-cab">
          <h2>Consumo</h2>
          <div className="ng-acoes"><input type="month" className="ng-select" value={mes} onChange={e => e.target.value && setMes(e.target.value)} aria-label="Mês" /></div>
        </div>
        <p className="ng-explic">{plural(totais.respostas, 'resposta', 'respostas')} · {nf.format(totais.tokens)} tokens no mês{agente.monthlyTokenLimit ? ` de ${nf.format(agente.monthlyTokenLimit)} do agente` : ''}.</p>
        <div className="ng-tabela">
          <table>
            <thead><tr><th>Pessoa</th><th className="n">Respostas</th><th className="n">Erros</th><th className="n">Tokens</th><th>Último uso</th></tr></thead>
            <tbody>
              {!consumo && <tr><td colSpan={5} className="vazio">Carregando…</td></tr>}
              {consumo?.length === 0 && <tr><td colSpan={5} className="vazio">Ninguém usou neste mês.</td></tr>}
              {consumo?.map(c => (
                <tr key={c.usuarioId}>
                  <td>{c.nome}</td><td className="n">{c.respostas}</td><td className="n">{c.erros}</td>
                  <td className="n">{nf.format(c.tokensTotal)}{c.limite ? ` de ${nf.format(c.limite)}` : ''}</td>
                  <td>{c.ultimoUso ? new Date(c.ultimoUso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Janela aberto={!!liberar} titulo="Liberar pessoa" sub={`Agente ${agente.name}`} botao="Liberar" desabilitado={!liberar?.pessoa || lerLimite(liberar.limite) === 'invalido'}
              aoConfirmar={confirmarLiberar} aoFechar={() => setLiberar(null)}>
        {liberar && (
          <>
            <div className="nv-campo"><span>Pessoa</span><BuscaPessoa valor={liberar.pessoa} aoEscolher={p => setLiberar({ ...liberar, pessoa: p })} /></div>
            <label className="nv-campo"><span>Limite mensal da pessoa (tokens)</span>
              <input value={liberar.limite} inputMode="numeric" onChange={e => setLiberar({ ...liberar, limite: e.target.value.replace(/\D/g, '') })} placeholder="Sem limite individual" />
            </label>
          </>
        )}
      </Janela>
    </>
  )
}
